import { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import { dataGridCol, gridCurrencyCell } from '@shared/components/grid/common';
import { formatString, gridDateTimeFormatter } from '@shared/commonFunctions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';

import { Wrapper } from '@styles/CommonStyles';
import { IProductCancellations } from './ImportProductCancel';
import { UnIdentifiedDealsModal } from './UnIdentifiedDealsModal';

const columns: GridColDef[] = [
  dataGridCol('contractNumber', {
    flex: 2,
    minWidth: 110,
  }),
  dataGridCol('product', {
    flex: 1,
    minWidth: 70,
  }),
  dataGridCol('contractHolder', {
    flex: 2,
    minWidth: 120,
  }),
  dataGridCol('vinLast8', {
    flex: 1,
    minWidth: 100,
    valueFormatter: ({ value }) => (value?.toUpperCase()),
  }),
  dataGridCol('saleDate', {
    flex: 1,
    minWidth: 150,
    type: 'dateTime',
    valueFormatter: gridDateTimeFormatter,
  }),
  dataGridCol('cancelDate', {
    flex: 1,
    minWidth: 150,
    type: 'dateTime',
    valueFormatter: gridDateTimeFormatter,
  }),
  dataGridCol('cancelFactor', {
    flex: 1,
    minWidth: 100,
  }),
  dataGridCol('cancelReason', {
    flex: 2,
    minWidth: 100,
  }),
  dataGridCol('originalCustomerCost', {
    flex: 1,
    minWidth: 100,
    ...gridCurrencyCell,
  }),
  dataGridCol('originalSellerCost', {
    flex: 1,
    minWidth: 100,
    ...gridCurrencyCell,
  }),
  dataGridCol('cancelFee', {
    flex: 1,
    minWidth: 100,
    ...gridCurrencyCell,
  }),
  dataGridCol('customerRefund', {
    flex: 1,
    ...gridCurrencyCell,
  }),
  dataGridCol('refundFromSeller', {
    flex: 1,
    ...gridCurrencyCell,
  }),
  dataGridCol('paymentAmount', {
    flex: 1,
    minWidth: 100,
    ...gridCurrencyCell,
  }),
].map((column) => ({ minWidth: 100, ...column }));

export const UnIdentifiedDeals = () => {
  const [selectedDeal, setSelectedDeal] = useState<IProductCancellations | null>(null);
  const [unIdentifiedDealModalPosition, setUnIdentifiedDealModalPosition] = useState(0);
  const { data = [], mutate } = useSWR<IProductCancellations[]>(
    `${SERVICES.ACCOUNTING}/productcancellations/checks?displayChecksValue=2`
  );

  const rows = useMemo(() => data.map((value) => ({ ...value, product: formatString(value.product) })), [data]);

  const handleRowClick = useCallback(({ row }: { row: IProductCancellations }) => {
    setSelectedDeal(row);
    setUnIdentifiedDealModalPosition(prevState => prevState + 1);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedDeal(null);
    setUnIdentifiedDealModalPosition(0);
  }, []);

  return (
    <>
      <Wrapper>
        <PhoenixGrid
          autoHeight
          withHeader
          noButton
          strict
          canNotEdit
          canNotCopy
          name="UnIdentified Deals"
          columns={columns}
          rows={rows || []}
          getRowId={(row) => row.productCancellationCheckId}
          onRowClick={handleRowClick}
        />
      </Wrapper>

      {selectedDeal && (
        <UnIdentifiedDealsModal
          unIdentifiedDealModalPosition={unIdentifiedDealModalPosition}
          selectedDeal={selectedDeal}
          refreshParent={mutate}
          onClose={handleClose}
        />
      )}
    </>
  );
};
